<script lang="ts">
  /// 会員 (公開サイトのマイページ登録者) 一覧 (U10)。
  ///
  /// 会員テナントの Auth0 を backend (U9) が直読した結果をそのまま表示する読み取り専用の画面。
  /// CRM の DB には会員を持たない (v1 は Auth0 が唯一の置き場) ため、紐づき状態や照合結果は
  /// 表示しない — 照合ルールは Phase 2 の 3 層データモデルの担当で、画面が先に答えを持たない。
  ///
  /// 社内担当者の「メンバー」画面 (`pages/members`) とは別物。
  import { onMount } from 'svelte';

  import { SITE_MEMBERS_PER_PAGE, siteMemberListStore } from '@entities/site-member/store.svelte';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import { canViewSiteMembers } from '@shared/types/session';
  import Pagination from '@shared/ui/Pagination.svelte';
  import SiteMemberTable from '@widgets/site-member-table/SiteMemberTable.svelte';

  /// Auth0 の user search を offset で辿れる範囲 (先頭 1000 件)。backend の `SEARCH_WINDOW`
  /// (`/apps/backend/crates/application/src/site_members.rs`) と対で、超えるページを要求すると
  /// 400 になる。UI 側でも同じ窓でページ送りを止め、押せるのに失敗するボタンを作らない。
  const SEARCH_WINDOW = 1000;

  /// 権限の UI ミラー (真実は backend の `Action::SiteMemberRead`)。readonly は不可。
  const allowView = $derived(!!sessionStore.user && canViewSiteMembers(sessionStore.user.role));

  const perPage = $derived(siteMemberListStore.data?.per_page ?? SITE_MEMBERS_PER_PAGE);
  /// 走査窓の中で辿れる最終ページ (Pagination と同じ 1 起点)。
  const maxPage = $derived(Math.max(1, Math.floor(SEARCH_WINDOW / perPage)));
  /// `total` が Auth0 の走査上限で頭打ちになるのか実数が返るのかは未確認 (U9)。`>` ではなく
  /// `>=` で見て、どちらの意味論でも注記が出るようにする。
  const atWindowLimit = $derived((siteMemberListStore.data?.total ?? 0) >= SEARCH_WINDOW);

  onMount(() => {
    // 権限が無いなら API を叩かない (backend も 403 で弾くが、無駄な往復とエラー表示を避ける)。
    if (allowView) void siteMemberListStore.refresh(0);
  });

  function changePage(uiPage: number): void {
    // API のページは 0 起点。1 起点との変換はここ 1 か所に閉じる。
    void siteMemberListStore.refresh(uiPage - 1);
  }
</script>

<header class="page-head">
  <h1>会員</h1>
  <p class="muted">
    公開サイトのマイページに登録した人の一覧です。会員基盤 (Auth0) から直接読んでいます。申告欄は会員本人がマイページで入力した内容で、CRM の確定情報ではありません。
  </p>
</header>

<section>
  {#if !allowView}
    <div class="panel"><p class="muted">会員一覧を表示する権限がありません。</p></div>
  {:else if siteMemberListStore.error}
    <div class="error">{siteMemberListStore.error}</div>
  {:else if siteMemberListStore.data}
    <div class="panel">
      {#if atWindowLimit}
        <p class="notice">
          会員基盤の仕様により、一覧で辿れるのは先頭 {SEARCH_WINDOW} 件までです (該当 {siteMemberListStore
            .data.total} 件)。
        </p>
      {/if}
      <SiteMemberTable members={siteMemberListStore.data.items} />
      <Pagination
        total={siteMemberListStore.data.total}
        page={siteMemberListStore.data.page + 1}
        pageSize={siteMemberListStore.data.per_page}
        {maxPage}
        onChange={changePage}
      />
    </div>
  {:else}
    <!-- onMount は初回描画の後に走るため、取得前のフレームがここに落ちる。 -->
    <div class="panel"><p class="muted">読み込み中…</p></div>
  {/if}
</section>

<style>
  .page-head {
    margin: 0 0 12px;
  }
  .page-head h1 {
    margin: 0;
    font-size: 1.25rem;
  }
  .page-head p {
    margin: 4px 0 0;
    font-size: 12px;
    max-width: 72ch;
  }
  .muted {
    color: var(--muted, #6b7280);
  }
  .notice {
    margin: 0 0 12px;
    padding: 8px 12px;
    border-radius: var(--radius, 6px);
    background: rgba(234, 179, 8, 0.1);
    color: #a16207;
    font-size: 12px;
  }
  .error {
    color: var(--danger, #c83232);
    background: rgba(200, 50, 50, 0.06);
    padding: 8px;
    border-radius: 4px;
    margin: 8px 0;
  }
</style>
